import { Controller } from "@hotwired/stimulus"
import db from "../field_db"
import { formatBytes } from "../lib/format_bytes"
import { photoPreviewBlob } from "../field_image_queue"

// Reads pending_responses and pending_images from IndexedDB and renders a
// grouped-by-response list so users can see what is queued for sync, with
// human-readable state reasons and correct colour/icon per state.
//
// Each group represents one responseId. A group may have a pending_responses
// row (giving placeName, missionName, queuedAt) and/or pending_images rows
// (giving count, sizeBytes, statuses). Groups where no pending_response row
// exists (orphaned images -- form submitted, images still uploading) are shown
// with "Unknown store" as the place name.
//
// Group state precedence:
//   failed    -- any failed response OR any failed image
//   uploading -- any uploading image (and no failures)
//   waiting   -- everything else still pending
//
// Targets:
//   container  -- rendered list of pending items
//   count      -- badge showing total pending item count
//   section    -- the outer wrapper (hidden when queue is empty)

export default class extends Controller {
  static targets = ["container", "count", "section"]

  async connect() {
    await this._render()

    this._refreshHandler = () => this._render()
    window.addEventListener("field:sync-complete", this._refreshHandler)
    window.addEventListener("field:online", this._refreshHandler)

    // Live per-image upload percentage (Card #3492) -- dispatched by
    // field_image_queue.js's foreground XHR upload path. A targeted DOM patch
    // rather than a full _render() per tick: a re-render replaces every
    // thumbnail (createObjectURL/revokeObjectURL churn) many times a second
    // while one upload is in flight, which is unnecessary work and thumbnail
    // flicker for a label that only needs its own text updated.
    this._progressHandler = (e) => this._onUploadProgress(e.detail)
    window.addEventListener("field:upload-progress", this._progressHandler)
  }

  disconnect() {
    window.removeEventListener("field:sync-complete", this._refreshHandler)
    window.removeEventListener("field:online", this._refreshHandler)
    window.removeEventListener("field:upload-progress", this._progressHandler)

    for (const url of (this._createdUrls || [])) {
      try { URL.revokeObjectURL(url) } catch { /* ok */ }
    }
  }

  // Patches the matching group's pill text in place with a live percentage.
  // Guarded to only touch a pill already showing "Uploading..." -- a stale
  // progress event arriving after the group has since failed, finished, or
  // been re-rendered into a different state must never resurrect the
  // uploading label.
  _onUploadProgress({ surveyableId, pct }) {
    if (!this.hasContainerTarget) return
    const card = this.containerTarget.querySelector(`[data-group-key="${CSS.escape(String(surveyableId))}"]`)
    const pill = card?.querySelector("[data-role='pill']")
    if (!pill || !pill.textContent.startsWith("Uploading")) return
    pill.textContent = `Uploading… ${pct}%`
  }

  async _render() {
    this._createdUrls = []

    const responses = await db.pending_responses.toArray()
    const images    = await db.pending_images.toArray()

    // Total pending item count (responses + non-uploaded images)
    const nonUploadedImages = images.filter(img => img.status !== "uploaded")
    const total = responses.length + nonUploadedImages.length

    if (this.hasCountTarget) {
      this.countTarget.textContent = total > 0 ? String(total) : ""
    }

    if (this.hasSectionTarget) {
      this.sectionTarget.classList.toggle("hidden", total === 0)
    }

    if (!this.hasContainerTarget) return
    if (total === 0) {
      this.containerTarget.innerHTML = ""
      return
    }

    // Build a map of responseId -> { response, images }
    const groups = new Map()

    for (const r of responses) {
      const key = String(r.responseId)
      if (!groups.has(key)) groups.set(key, { response: null, images: [] })
      groups.get(key).response = r
    }

    for (const img of nonUploadedImages) {
      const key = String(img.surveyableId)
      if (!groups.has(key)) groups.set(key, { response: null, images: [] })
      groups.get(key).images.push(img)
    }

    const online = localStorage.getItem("field:online") !== "false"
    const cards  = []

    for (const [key, group] of groups) {
      cards.push(await this._groupCard(key, group, online))
    }

    this.containerTarget.innerHTML = cards.join("")
  }

  async _groupCard(key, group, online) {
    const { response, images } = group

    const placeName   = this._esc(response?.placeName   || "Unknown store")
    const missionName = this._esc(response?.missionName || "")
    const queuedAt    = response?.queuedAt ? new Date(response.queuedAt) : null

    // Determine group state
    const hasFailed    = (response?.status === "failed") || images.some(img => img.status === "failed")
    const hasUploading = !hasFailed && images.some(img => img.status === "uploading")
    const state        = hasFailed ? "failed" : hasUploading ? "uploading" : "waiting"

    // Icon, colour, and label per state (hardcoded -- no dynamic Tailwind strings)
    let iconHtml, pillClass, pillLabel

    if (state === "failed") {
      iconHtml  = this._stateIcon("failed")
      pillClass = "text-red-600 bg-red-50"
      pillLabel = "Upload failed"
    } else if (state === "uploading") {
      iconHtml  = this._stateIcon("uploading")
      pillClass = "text-sapphire bg-sapphire/10"
      pillLabel = "Uploading..."
    } else {
      // waiting
      iconHtml = this._stateIcon("waiting")
      if (online) {
        pillClass = "text-amber-600 bg-amber-50"
        pillLabel = "Waiting to upload"
      } else {
        pillClass = "text-slate-500 bg-slate-100"
        pillLabel = "Waiting for signal"
      }
    }

    // Photo line
    let photoLine = ""
    if (images.length > 0) {
      const totalBytes  = images.reduce((sum, img) => sum + (img.sizeBytes || 0), 0)
      const countLabel  = `${images.length} ${this._plural(images.length, "photo")}`
      const sizeLabel   = totalBytes > 0 ? formatBytes(totalBytes) : ""
      photoLine = sizeLabel
        ? `<p class="text-xs text-slate-400 mt-0.5">${this._esc(countLabel)} · ${this._esc(sizeLabel)}</p>`
        : `<p class="text-xs text-slate-400 mt-0.5">${this._esc(countLabel)}</p>`
    }

    // Secondary text: missionName + queued time
    const timePart      = queuedAt ? this._timeAgo(queuedAt) : ""
    const subtitleParts = [missionName, timePart].filter(Boolean)
    const subtitleLine  = subtitleParts.length > 0
      ? `<p class="text-xs text-slate-400 mt-0.5">${subtitleParts.join(" · ")}</p>`
      : ""

    // Thumbnail from first image blob (if available)
    let thumbHtml = ""
    // Card #3995 -- vault rows hold no Blob; read the first one's bytes back.
    let previewBlob = null
    for (const img of images) {
      previewBlob = await photoPreviewBlob(img)
      if (previewBlob) break
    }
    if (previewBlob) {
      try {
        const url = URL.createObjectURL(previewBlob)
        this._createdUrls.push(url)
        thumbHtml = `<img src="${url}" class="shrink-0 size-10 rounded-lg object-cover bg-slate-100" />`
      } catch { /* fall through */ }
    }

    if (!thumbHtml) {
      thumbHtml = `
        <div class="shrink-0 size-10 rounded-lg bg-slate-100 flex items-center justify-center">
          ${iconHtml}
        </div>
      `
    }

    return `
      <div class="flex items-start gap-3 px-4 py-3 border-b border-slate-50 last:border-0" data-group-key="${this._esc(key)}">
        ${thumbHtml}
        <div class="flex-1 min-w-0">
          <p class="text-sm font-semibold text-slate-700 truncate">${placeName}</p>
          ${subtitleLine}
          ${photoLine}
        </div>
        <span class="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full ${pillClass}" data-role="pill">${this._esc(pillLabel)}</span>
      </div>
    `
  }

  // Inline icons per state (16×16, Heroicons style)
  _stateIcon(state) {
    if (state === "failed") {
      return `
        <svg class="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
        </svg>
      `
    }
    if (state === "uploading") {
      return `
        <svg class="w-4 h-4 text-sapphire" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
        </svg>
      `
    }
    // waiting (clock)
    return `
      <svg class="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
    `
  }

  _timeAgo(date) {
    const seconds = Math.floor((Date.now() - date) / 1000)
    if (seconds < 60)  return "just now"
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60)  return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24)    return `${hours}h ago`
    const days = Math.floor(hours / 24)
    return `${days}d ago`
  }

  _esc(str) {
    return String(str)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }

  _plural(n, word) {
    return n === 1 ? word : `${word}s`
  }
}
